/// RYA chartwork catalogue — split into SYMBOLS (visual marks you
/// draw on the chart) and TECHNIQUES (the procedures that produce
/// them).
///
/// Symbols are the simple shapes: position fix (circle around dot),
/// DR tick, EP triangle, waypoint square, plus the chevron
/// annotations on track lines (water / ground / tidal). They drive
/// the recognition flashcards game.
///
/// Techniques are the workflows — Dead Reckoning (DR), Estimated
/// Position (EP), and Course To Steer (CTS) — that produce those
/// marks on a chart. Each technique has step-by-step instructions
/// and a full diagram component (DrDiagram, EpDiagram, CtsDiagram).
///
/// Track colours in our diagrams follow the RYA Day Skipper /
/// Yachtmaster diagram convention so a student can tell the marks
/// apart at a glance: orange for the water track / DR, magenta for
/// the EP, green for a CTS line. On a real chart you'd use a sharp
/// pencil — what matters is the symbol (the half-arrow, the
/// triangle, the chevrons), not the colour you draw it in.

export type SymbolKind = "fix" | "dr_tick" | "ep_triangle" | "waypoint" | "chevron";

export type TrackColour = "orange" | "magenta" | "green" | "black";

export interface ChartworkSymbol {
  /// Stable answer key for flashcards.
  id: string;
  /// Short label ("DR tick", "EP triangle", "Tidal vector").
  name: string;
  /// Long-form abbreviation expansion or `null` when name is already
  /// the full term (e.g. Waypoint).
  abbreviation: string | null;
  /// One-line plain-English meaning for the reveal.
  meaning: string;
  kind: SymbolKind;
  /// For chevrons: how many chevron strokes to draw (1=water,
  /// 2=ground, 3=tidal). Ignored for other kinds.
  chevronCount?: 1 | 2 | 3;
  /// Mark / annotation colour. Defaults to black for shapes that
  /// don't carry a track colour (Fix, Waypoint, chevrons).
  colour: TrackColour;
  /// When you'd use this symbol — supplements `meaning` in the
  /// reveal panel with the operational "why".
  use: string;
}

export const CHARTWORK_SYMBOLS: Readonly<Record<string, ChartworkSymbol>> =
  Object.freeze({
    fix: {
      id: "fix",
      name: "Position fix",
      abbreviation: null,
      meaning: "Confirmed position — circle around a dot, time noted alongside.",
      kind: "fix",
      colour: "black",
      use: "Drawn whenever you have a confirmed position from bearings, GPS, or a transit.",
    },
    dr_tick: {
      id: "dr_tick",
      name: "DR tick",
      abbreviation: "Dead-Reckoning marker",
      meaning:
        "A perpendicular tick across the water-track line marking a Dead Reckoning position. Shown in orange in our diagrams.",
      kind: "dr_tick",
      colour: "orange",
      use: "Marks the projected position on the track line using course steered + log distance only.",
    },
    ep_triangle: {
      id: "ep_triangle",
      name: "EP triangle",
      abbreviation: "Estimated-Position marker",
      meaning:
        "A small triangle around a dot marking the Estimated Position. Shown in magenta in our diagrams.",
      kind: "ep_triangle",
      colour: "magenta",
      use: "Drawn at the end of the tidal-vector arm from a DR, when no fix is available.",
    },
    waypoint: {
      id: "waypoint",
      name: "Waypoint",
      abbreviation: null,
      meaning: "A planned point on a passage — square around a dot.",
      kind: "waypoint",
      colour: "black",
      use: "Mark turning points along a planned route; usually labelled WP1, WP2 etc.",
    },
    water_track: {
      id: "water_track",
      name: "Water track",
      abbreviation: null,
      meaning:
        "Direction the vessel moves through the water — single chevron.",
      kind: "chevron",
      chevronCount: 1,
      colour: "black",
      use: "Annotates a heading-and-log line on the chart. In our diagrams a green water-track is a Course To Steer plotted in advance.",
    },
    ground_track: {
      id: "ground_track",
      name: "Ground track",
      abbreviation: null,
      meaning:
        "Direction the vessel moves over the ground — double chevron.",
      kind: "chevron",
      chevronCount: 2,
      colour: "black",
      use: "Annotates the line from one fix or EP to the next.",
    },
    tidal_vector: {
      id: "tidal_vector",
      name: "Tidal vector",
      abbreviation: null,
      meaning:
        "Set and drift of the tidal stream — triple chevron.",
      kind: "chevron",
      chevronCount: 3,
      colour: "black",
      use: "Applied from a DR position to derive the EP, or used in a CTS calculation.",
    },
  });

export const CHARTWORK_IDS: readonly string[] = Object.keys(CHARTWORK_SYMBOLS);

/// Chartwork TECHNIQUES — the procedures that produce the chart
/// symbols above. Each has a step-by-step list and a diagram
/// component (rendered in apps/web by id-dispatch).
export interface ChartworkTechnique {
  /// Stable id matching the technique-diagram component ("dr",
  /// "ep", "cts").
  id: "dr" | "ep" | "cts";
  /// Full name with abbreviation in parentheses.
  name: string;
  abbreviation: string;
  /// One-line summary of what the technique produces.
  summary: string;
  /// Step-by-step construction on the chart.
  steps: readonly string[];
  /// When you'd reach for this technique on passage.
  whenToUse: string;
  /// Colour the technique uses on the chart.
  colour: TrackColour;
}

export const CHARTWORK_TECHNIQUES: Readonly<
  Record<"dr" | "ep" | "cts", ChartworkTechnique>
> = Object.freeze({
  dr: {
    id: "dr",
    name: "Dead Reckoning",
    abbreviation: "DR",
    summary:
      "Project your position from the last fix using course steered + log distance only — no allowance for tide or leeway.",
    steps: [
      "From your last fix, draw a line along the course you have been steering (the water track) — single chevron.",
      "Measure the distance run from the log (or from boat speed × time).",
      "Step that distance off along the line with dividers — that point is the DR.",
      "Mark the DR with a perpendicular tick across the line and label with the time + log reading. (Shown in orange in the diagram opposite.)",
    ],
    whenToUse:
      "Quick interim position estimate when tide is negligible OR when you need a baseline before adding the tidal vector to find an EP.",
    colour: "orange",
  },
  ep: {
    id: "ep",
    name: "Estimated Position",
    abbreviation: "EP",
    summary:
      "DR plus the tidal vector and leeway — the best position estimate without a confirmed fix.",
    steps: [
      "Start with the DR for the time you want the EP.",
      "From the DR, draw the tidal vector for that hour — set + drift × time, with TRIPLE chevrons.",
      "If sailing with measurable leeway, apply a small additional vector downwind.",
      "The end of the tidal-vector arm is the EP. Mark it with a small triangle around a dot and label with the time. (Shown in magenta in the diagram opposite.)",
      "The line from the previous fix to the EP is the ground track — double chevron.",
    ],
    whenToUse:
      "Hourly position-keeping when no fix is available — coastal passage in tide, offshore between sights, fog without GPS.",
    colour: "magenta",
  },
  cts: {
    id: "cts",
    name: "Course To Steer",
    abbreviation: "CTS",
    summary:
      "The heading to steer that — with tide and boat speed — gives the desired ground track from A to B. The classic tidal-triangle construction.",
    steps: [
      "Draw the desired ground track from A to B — double chevron.",
      "From A, draw the tidal vector that applies for the PERIOD of the passage (set + total drift over the time you expect to take). TRIPLE chevron. Label the far end C.",
      "Set dividers to the distance you will travel through the water in that period (boat speed × time).",
      "With dividers on C, swing an arc to intersect the ground track A→B. Call that intersection point D.",
      "Draw the line from C to D — this is your water track, the heading you must steer. SINGLE chevron. The bearing of C→D is the CTS. (Shown in green in the diagram opposite.)",
    ],
    whenToUse:
      "Passage planning — compute CTS at the chart table BEFORE you set off so you can steer the right heading from the moment you cast off.",
    colour: "green",
  },
});

export const CHARTWORK_TECHNIQUE_IDS: readonly ("dr" | "ep" | "cts")[] = [
  "dr",
  "ep",
  "cts",
];
